import type {
  HsBoundingBoxView,
  HsFormatDecoder,
  HsFormatRegistry,
  HsFrustumRequestInfo,
  HsFrustumView,
  HsGeometryComponentView,
  HsHlodTilesetLink,
  HsMaterialView,
  HsNodeSmallView,
  HsNodesVisibilityView,
  HsNodeView,
  HsOriginalMeshSource,
  HsSceneNodeFilterResultView,
  HsSceneView,
  HsTransport,
  HsTransportRequest,
  HsTransportResponse,
} from '@sibe/hyperscene';
import { hsEnsureOk } from '../../../shared/http/api-error.function';

// Media types are repeated here instead of imported from the format addons so this client does not
// bundle them; the decoders come from the registry the host fills with format plugins.
const FRUSTUM_V4_CONTENT_TYPE: string = 'application/vnd.hyperscene.frustum.v4';
const FRUSTUM_JSON_CONTENT_TYPE: string = 'application/json';
const MATERIALS_V1_CONTENT_TYPE: string = 'application/vnd.hyperscene.materials.v1';
const JSON_CONTENT_TYPE: string = 'application/json';
const TEXTURE_BATCH_LIMIT: number = 100;

export type HsGeometryKind = 'Meshes' | 'Lines' | 'PointClouds';
/**
 * `quantized` and `quantized2` are for point clouds only: 16-bit positions in the block box (about 9 bytes per
 * point), or positions with as many bits as the block's spacing needs and RGB565 colors (about 6 bytes).
 */
export type HsGeometryPayloadFormat = 'binary' | 'draco' | 'quantized' | 'quantized2';

export interface HsViewerApiOptions {
  transport: HsTransport;
  /** Decoders for Frustum and Materials responses; install `HsFrustumFormat` and `HsMaterialsFormat`. */
  formats: HsFormatRegistry;
}

export interface HsGeometryRequestOptions {
  format?: HsGeometryPayloadFormat;
  signal?: AbortSignal;
}

export interface HsFrustumRequestOptions {
  /** Response layouts in preference order; defaults to every registered Frustum layout, binary first. */
  accept?: readonly string[];
  signal?: AbortSignal;
}

/** Read and visibility endpoints the viewer needs to display a scene. */
export class HsViewerApi {
  private readonly transport: HsTransport;
  private readonly formats: HsFormatRegistry;

  public constructor(options: HsViewerApiOptions) {
    this.transport = options.transport;
    this.formats = options.formats;
  }

  public getScene(sceneId: string): Promise<HsSceneView> {
    return this.json<HsSceneView>({ path: `api/Scenes/${id(sceneId)}` });
  }

  /** Union of the mesh, line and point cloud bounds; `null` for an empty scene. */
  public async getSceneBoundingBox(sceneId: string): Promise<HsBoundingBoxView | null> {
    const boxes: { value: HsBoundingBoxView | null }[] = await Promise.all(
      (['Meshes', 'Lines', 'PointClouds'] as const).map((geometryFilter: HsGeometryKind) =>
        this.json<{ value: HsBoundingBoxView | null }>({
          path: `api/Scenes/${id(sceneId)}/Search/BoundingBox`,
          method: 'POST',
          body: { searchValue: '', geometryFilter },
        })
      )
    );
    const bounds: HsBoundingBoxView[] = boxes.flatMap((box: { value: HsBoundingBoxView | null }) =>
      box.value === null ? [] : [box.value]
    );
    if (bounds.length === 0) {
      return null;
    }
    if (bounds.some((box: HsBoundingBoxView) => !isValidBox(box))) {
      throw new Error('Invalid scene bounding box.');
    }
    return {
      min: [0, 1, 2].map((axis: number) => Math.min(...bounds.map((box: HsBoundingBoxView) => box.min[axis]))),
      max: [0, 1, 2].map((axis: number) => Math.max(...bounds.map((box: HsBoundingBoxView) => box.max[axis]))),
    };
  }

  public async getNodesBoundingBox(nodeIds: readonly string[]): Promise<HsBoundingBoxView | null> {
    const result: { value: HsBoundingBoxView | null } = await this.json<{ value: HsBoundingBoxView | null }>({
      path: 'api/Nodes/BoundingBox',
      method: 'POST',
      body: nodeIds,
    });
    return result.value;
  }

  public getSceneRootNodes(sceneId: string, nodeTags: readonly string[] = []): Promise<HsNodeSmallView[]> {
    const tags: string[] = nodeTags.filter((tag: string) => tag.trim().length > 0);
    return this.json<HsNodeSmallView[]>({
      path: `api/Scenes/${id(sceneId)}/Nodes`,
      query: { nodeTags: tags.length > 0 ? tags : undefined },
    });
  }

  public getNode(nodeId: string): Promise<HsNodeView> {
    return this.json<HsNodeView>({ path: `api/Nodes/${id(nodeId)}` });
  }

  public getNodeChildren(nodeId: string): Promise<HsNodeSmallView[]> {
    return this.json<HsNodeSmallView[]>({ path: `api/Nodes/${id(nodeId)}/Children` });
  }

  public getNodeChildrenBulk(nodeIds: readonly string[]): Promise<HsNodeSmallView[]> {
    return this.json<HsNodeSmallView[]>({ path: 'api/Nodes/Children/Bulk', method: 'POST', body: nodeIds });
  }

  /** Every descendant of the nodes, at any depth. */
  public getNodesAllChildren(nodeIds: readonly string[]): Promise<HsNodeSmallView[]> {
    return this.json<HsNodeSmallView[]>({ path: 'api/Nodes/AllChildren', method: 'POST', body: nodeIds });
  }

  /** Ids from the tree root down to the node. */
  public getNodeRoute(nodeId: string): Promise<string[]> {
    return this.json<string[]>({ path: `api/Nodes/${id(nodeId)}/Route` });
  }

  public getNodeGeometryIds(nodeId: string, filter?: HsGeometryKind): Promise<string[]> {
    return this.json<string[]>({ path: `api/Nodes/${id(nodeId)}/GeometryIds`, query: { filter } });
  }

  public getNodeTreeGeometries(nodeId: string, filter: HsGeometryKind): Promise<HsGeometryComponentView[]> {
    return this.json<HsGeometryComponentView[]>({ path: `api/Nodes/${id(nodeId)}/Geometries`, query: { filter } });
  }

  public getNodeTreeOriginalMaterials(rootNodeId: string): Promise<HsMaterialView[]> {
    return this.json<HsMaterialView[]>({ path: `api/Nodes/${id(rootNodeId)}/Materials` });
  }

  /** Materials v1 for the given ids, decoded by the registered Materials format. */
  public async getMaterials(materialIds: readonly string[]): Promise<HsMaterialView[]> {
    if (materialIds.length === 0) {
      return [];
    }
    // The payload refers to materials by request position, so the ids are frozen before awaiting.
    const requestedIds: string[] = materialIds.slice();
    const decoder: HsFormatDecoder<HsMaterialView[]> = this.requireDecoder(MATERIALS_V1_CONTENT_TYPE, 'Materials');
    const response: HsTransportResponse = await this.send({
      path: 'api/Materials/Bulk/Get',
      method: 'POST',
      accept: [MATERIALS_V1_CONTENT_TYPE],
      body: requestedIds,
    });
    if (response.contentType !== MATERIALS_V1_CONTENT_TYPE) {
      throw new Error(
        `Unsupported binary Materials content type: ${response.contentType !== '' ? response.contentType : 'undefined'}`
      );
    }
    return decoder.decode(await response.arrayBuffer(), {
      contentType: response.contentType,
      request: requestedIds,
    });
  }

  public async getDocument(documentId: string): Promise<Blob> {
    return (await this.send({ path: `api/Documents/${id(documentId)}/Download` })).blob();
  }

  /** Downloads 1-100 distinct documents (textures, HLOD nodes) in one request, keeping their media types. */
  public async getDocuments(documentIds: readonly string[]): Promise<Map<string, Blob>> {
    if (
      documentIds.length < 1 ||
      documentIds.length > TEXTURE_BATCH_LIMIT ||
      new Set(documentIds).size !== documentIds.length
    ) {
      throw new Error(`Texture batch must contain 1-${TEXTURE_BATCH_LIMIT} distinct ids.`);
    }
    const response: HsTransportResponse = await this.send({
      path: 'api/Documents/Batch/Download',
      method: 'POST',
      accept: [JSON_CONTENT_TYPE],
      body: documentIds,
    });
    return parseDocumentBatch(await response.arrayBuffer(), documentIds);
  }

  public async hasPhotogrammetry(sceneId: string): Promise<boolean> {
    const result: { hasPhotogrammetry: boolean } = await this.json<{ hasPhotogrammetry: boolean }>({
      path: `api/Geometries/Scenes/${id(sceneId)}/PhotogrammetryProgressive`,
    });
    return result.hasPhotogrammetry;
  }

  public getPhotogrammetryTilesets(meshIds: readonly string[]): Promise<HsHlodTilesetLink[]> {
    if (meshIds.length === 0) {
      return Promise.resolve([]);
    }
    return this.json<HsHlodTilesetLink[]>({
      path: 'api/Geometries/Meshes/PhotogrammetryProgressive',
      method: 'POST',
      body: meshIds,
    });
  }

  public getOriginalMeshSources(nodeIds: readonly string[]): Promise<HsOriginalMeshSource[]> {
    return this.json<HsOriginalMeshSource[]>({
      path: 'api/Geometries/Meshes/OriginalSources',
      method: 'POST',
      body: nodeIds,
    });
  }

  /** Raw geometry payload; decode it with `formats/geometry` or `formats/draco`. */
  public async getGeometryPayload(
    kind: HsGeometryKind,
    geometryIds: readonly string[],
    options: HsGeometryRequestOptions = {}
  ): Promise<ArrayBuffer> {
    if (geometryIds.length === 0) {
      return new ArrayBuffer(0);
    }
    // Lines have no compressed variant, so their endpoint takes the bare id array.
    const body: unknown = kind === 'Lines' ? geometryIds : { geometryIds, format: options.format ?? 'binary' };
    const response: HsTransportResponse = await this.send({
      path: `api/Geometries/Binary/${kind}`,
      method: 'POST',
      accept: [JSON_CONTENT_TYPE],
      body,
      signal: options.signal,
    });
    return response.arrayBuffer();
  }

  public hideNodes(sceneId: string, nodeIds: readonly string[]): Promise<void> {
    return this.noContent({ path: `api/Scenes/${id(sceneId)}/Nodes/Hide`, method: 'POST', body: nodeIds });
  }

  public unhideNodes(sceneId: string, nodeIds: readonly string[]): Promise<void> {
    return this.noContent({ path: `api/Scenes/${id(sceneId)}/Nodes/Unhide`, method: 'POST', body: nodeIds });
  }

  public isolateNodes(sceneId: string, nodeIds: readonly string[]): Promise<void> {
    return this.noContent({ path: `api/Scenes/${id(sceneId)}/Nodes/Isolate`, method: 'POST', body: nodeIds });
  }

  public unhideAllNodes(sceneId: string): Promise<void> {
    return this.noContent({
      path: `api/Scenes/${id(sceneId)}/Nodes/Unhide/All`,
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'Content-Type': JSON_CONTENT_TYPE },
    });
  }

  /**
   * Hides node subtrees for one viewer session only (`HsSceneSource.viewSessionId`); other viewers of the scene and
   * the scene's own visibility are not changed.
   */
  public hideSessionNodes(sceneId: string, viewSessionId: string, nodeIds: readonly string[]): Promise<void> {
    return this.noContent({ path: `${sessionPath(sceneId, viewSessionId)}/Nodes/Hide`, method: 'POST', body: nodeIds });
  }

  /** Shows node subtrees and their ancestors for one viewer session only. */
  public unhideSessionNodes(sceneId: string, viewSessionId: string, nodeIds: readonly string[]): Promise<void> {
    return this.noContent({
      path: `${sessionPath(sceneId, viewSessionId)}/Nodes/Unhide`,
      method: 'POST',
      body: nodeIds,
    });
  }

  /** Hides everything but the node subtrees for one viewer session only. */
  public isolateSessionNodes(sceneId: string, viewSessionId: string, nodeIds: readonly string[]): Promise<void> {
    return this.noContent({
      path: `${sessionPath(sceneId, viewSessionId)}/Nodes/Isolate`,
      method: 'POST',
      body: nodeIds,
    });
  }

  /** Shows every node for one viewer session, including nodes the scene hides. */
  public unhideAllSessionNodes(sceneId: string, viewSessionId: string): Promise<void> {
    return this.noContent({
      path: `${sessionPath(sceneId, viewSessionId)}/Nodes/Unhide/All`,
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'Content-Type': JSON_CONTENT_TYPE },
    });
  }

  /** Drops a viewer session's own visibility: it sees the scene's visibility again. */
  public resetSessionVisibility(sceneId: string, viewSessionId: string): Promise<void> {
    return this.noContent({ path: `${sessionPath(sceneId, viewSessionId)}/Visibility`, method: 'DELETE' });
  }

  /** Visibility of the nodes in the scene, with a viewer session's own visibility on top when given. */
  public getNodesVisibility(
    sceneId: string,
    nodeIds: readonly string[],
    viewSessionId?: string
  ): Promise<HsNodesVisibilityView> {
    return this.json<HsNodesVisibilityView>({
      path: `api/Scenes/${id(sceneId)}/Nodes/State`,
      method: 'POST',
      query: { viewSessionId },
      body: nodeIds,
    });
  }

  /** Objects to add and remove for the camera in `request`, decoded by the registered Frustum format. */
  public async getFrustum(
    sceneId: string,
    request: HsFrustumRequestInfo,
    options: HsFrustumRequestOptions = {}
  ): Promise<HsFrustumView> {
    const accept: string[] = this.formats.accepted(
      options.accept ?? [FRUSTUM_V4_CONTENT_TYPE, FRUSTUM_JSON_CONTENT_TYPE]
    );
    if (accept.length === 0) {
      throw new Error('No Frustum format is installed; install HsFrustumFormat from @sibe/hyperscene/formats/frustum.');
    }
    const response: HsTransportResponse = await this.send({
      path: `api/Scenes/${id(sceneId)}/FrustumCompact`,
      method: 'POST',
      accept,
      body: request,
      signal: options.signal,
    });
    if (!accept.includes(response.contentType)) {
      throw new Error(
        `Unsupported compact Frustum content type: ${response.contentType !== '' ? response.contentType : 'undefined'}`
      );
    }
    const decoder: HsFormatDecoder<HsFrustumView> = this.requireDecoder(response.contentType, 'Frustum');
    return decoder.decode(await response.arrayBuffer(), { contentType: response.contentType, signal: options.signal });
  }

  public getAttributeValues(sceneId: string, attributeName: string): Promise<string[]> {
    return this.json<string[]>({ path: `api/Scenes/${id(sceneId)}/AttributeValues`, query: { name: attributeName } });
  }

  /** Scene nodes whose attribute `attributeName` contains `attributeValue`, paged. */
  public filterNodesByAttribute(
    sceneId: string,
    attributeName: string,
    attributeValue: string,
    page: { take: number; skip: number }
  ): Promise<HsSceneNodeFilterResultView[]> {
    return this.json<HsSceneNodeFilterResultView[]>({
      path: `api/Scenes/${id(sceneId)}/Nodes/Filter`,
      method: 'POST',
      query: { take: page.take, skip: page.skip },
      body: {
        groupFilter: {
          type: 'NodeFilterGroupInfo',
          unionType: 0,
          filters: [
            {
              type: 'NodeAttributeFilterInfo',
              name: attributeName,
              nameCompareType: 2,
              value: attributeValue,
              valueCompareType: 4,
            },
          ],
        },
        attributeName,
      },
    });
  }

  private requireDecoder<T>(contentType: string, label: string): HsFormatDecoder<T> {
    const decoder: HsFormatDecoder<T> | null = this.formats.find<T>(contentType);
    if (decoder === null) {
      throw new Error(`No ${label} format is installed for ${contentType}.`);
    }
    return decoder;
  }

  private async send(request: HsTransportRequest): Promise<HsTransportResponse> {
    return hsEnsureOk(await this.transport.request(request));
  }

  private async json<T>(request: HsTransportRequest): Promise<T> {
    return (await this.send({ ...request, accept: [JSON_CONTENT_TYPE] })).json<T>();
  }

  private async noContent(request: HsTransportRequest): Promise<void> {
    await this.send({ ...request, accept: [JSON_CONTENT_TYPE] });
  }
}

function id(value: string): string {
  return encodeURIComponent(value);
}

function isValidBox(box: HsBoundingBoxView): boolean {
  return (
    box.min.length === 3 &&
    box.max.length === 3 &&
    box.min.every(Number.isFinite) &&
    box.max.every(Number.isFinite) &&
    box.min.every((value: number, axis: number) => value <= box.max[axis])
  );
}

// Batch layout per record: 36-byte id, uint16 media type length, uint32 payload length (little endian),
// media type, payload.
function parseDocumentBatch(data: ArrayBuffer, documentIds: readonly string[]): Map<string, Blob> {
  const view: DataView = new DataView(data);
  const decoder: TextDecoder = new TextDecoder();
  const requested: Set<string> = new Set(documentIds);
  const documents: Map<string, Blob> = new Map();
  let offset: number = 0;
  while (offset < data.byteLength) {
    if (offset + 42 > data.byteLength) {
      throw new Error('Truncated texture batch header.');
    }
    const documentId: string = decoder.decode(new Uint8Array(data, offset, 36));
    const mimeLength: number = view.getUint16(offset + 36, true);
    const payloadLength: number = view.getUint32(offset + 38, true);
    offset += 42;
    if (
      offset + mimeLength + payloadLength > data.byteLength ||
      !requested.has(documentId) ||
      documents.has(documentId)
    ) {
      throw new Error(`Invalid texture batch record for ${documentId}.`);
    }
    const contentType: string = decoder.decode(new Uint8Array(data, offset, mimeLength));
    offset += mimeLength;
    documents.set(documentId, new Blob([data.slice(offset, offset + payloadLength)], { type: contentType }));
    offset += payloadLength;
  }
  if (documents.size !== requested.size) {
    throw new Error(`Texture batch returned ${documents.size} of ${requested.size} requested documents.`);
  }
  return documents;
}

function sessionPath(sceneId: string, viewSessionId: string): string {
  return `api/Scenes/${id(sceneId)}/Sessions/${id(viewSessionId)}`;
}
